import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftOutlined, CheckCircleOutlined, ReloadOutlined, CopyOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { WordPressConfig } from '../../types/wordpress.type';
import { wordpressInstanceTypes, phpVersions, wpVersions, environments } from '../../constants/wordpressConfig';

const CreateWordPress: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [config, setConfig] = useState<WordPressConfig>({
    name: '',
    domain: '',
    siteTitle: '',
    siteDescription: '',
    adminEmail: '',
    instanceType: '',
    storage: 20,
    enableCDN: true,
    autoScaling: false,
    phpVersion: 'php-8.2',
    wpVersion: 'wp-latest',
    sslEnabled: true,
    backupFrequency: 'daily',
    environment: 'production',
    adminUsername: 'admin',
    adminPassword: '',
    firewallEnabled: true,
    ddosProtection: true,
  });

  // Función para generar contraseña segura
  const generatePassword = () => {
    const length = 16;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+-=[]{}|;:,.<>?';
    let password = '';
    
    // Asegurar al menos un carácter de cada tipo
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const special = '!@#$%^&*()_+-=';
    
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += special[Math.floor(Math.random() * special.length)];
    
    // Completar el resto
    for (let i = password.length; i < length; i++) {
      password += charset[Math.floor(Math.random() * charset.length)];
    }
    
    // Mezclar caracteres
    password = password.split('').sort(() => Math.random() - 0.5).join('');
    
    setConfig({ ...config, adminPassword: password });
    setShowPassword(true);
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(config.adminPassword);
    alert('Password copied to clipboard!');
  };

  const handleNext = () => {
    if (step < 4) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleCreate = () => {
    navigate('/resources/wordpress-deployment', { state: { config } });
  };

  const isStepValid = () => {
    switch (step) {
      case 1:
        return config.name && config.domain && config.siteTitle && config.adminEmail;
      case 2:
        return config.instanceType;
      case 3:
        return true;
      case 4:
        return config.adminUsername && config.adminPassword && config.adminPassword.length >= 8;
      default:
        return false;
    }
  };

  // Función para calcular la fuerza de la contraseña
  const getPasswordStrength = (password: string) => {
    if (!password) return { strength: 0, label: '', color: '' };
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;
    
    if (strength <= 2) return { strength: 33, label: 'Weak', color: 'bg-red-500' };
    if (strength <= 4) return { strength: 66, label: 'Medium', color: 'bg-yellow-500' };
    return { strength: 100, label: 'Strong', color: 'bg-green-500' };
  };

  const passwordStrength = getPasswordStrength(config.adminPassword);

  return (
    <div className="max-w-[900px] mx-auto px-6">
      <button
        onClick={() => navigate('/resources')}
        className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 transition-colors"
      >
        <ArrowLeftOutlined />
        <span className="text-sm font-medium">Back to Resources</span>
      </button>

      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Create WordPress Site</h1>
      <p className="text-gray-600 dark:text-gray-400 mb-8">Deploy a managed WordPress hosting environment</p>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {[
            { num: 1, label: 'Basic Info' },
            { num: 2, label: 'Resources' },
            { num: 3, label: 'Advanced' },
            { num: 4, label: 'Security & Review' },
          ].map((s, idx) => (
            <div key={s.num} className="flex items-center flex-1">
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                    step >= s.num ? 'bg-black text-white dark:bg-white dark:text-black' : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                  }`}
                >
                  {step > s.num ? <CheckCircleOutlined /> : s.num}
                </div>
                <span
                  className={`text-xs mt-2 font-medium ${
                    step >= s.num ? 'text-gray-900 dark:text-white' : 'text-gray-500'
                  }`}
                >
                  {s.label}
                </span>
              </div>
              {idx < 3 && (
                <div
                  className={`h-1 flex-1 mx-2 rounded transition-all ${
                    step > s.num ? 'bg-black dark:bg-white' : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Form Content */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 mb-6">
        {/* Step 1: Basic Info */}
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Site Name *</label>
              <input
                type="text"
                value={config.name}
                onChange={(e) => setConfig({ ...config, name: e.target.value })}
                placeholder="my-wordpress-site"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent outline-none bg-white dark:bg-gray-800 dark:text-white"
              />
              <p className="text-xs text-gray-500 mt-1">Choose a unique name for your WordPress site</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Domain *</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={config.domain.replace('.kubiy.cloud', '')}
                  onChange={(e) => setConfig({ ...config, domain: `${e.target.value}.kubiy.cloud` })}
                  placeholder="my-blog"
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent outline-none bg-white dark:bg-gray-800 dark:text-white"
                />
                <span className="flex items-center text-gray-500 font-medium">.kubiy.cloud</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Your site will be accessible at this URL</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Site Title *</label>
              <input
                type="text"
                value={config.siteTitle}
                onChange={(e) => setConfig({ ...config, siteTitle: e.target.value })}
                placeholder="My Awesome Blog"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent outline-none bg-white dark:bg-gray-800 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Site Description</label>
              <textarea
                value={config.siteDescription}
                onChange={(e) => setConfig({ ...config, siteDescription: e.target.value })}
                placeholder="A brief description of your site"
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent outline-none bg-white dark:bg-gray-800 dark:text-white resize-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Admin Email *</label>
              <input
                type="email"
                value={config.adminEmail}
                onChange={(e) => setConfig({ ...config, adminEmail: e.target.value })}
                placeholder="admin@kubiy.cloud"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent outline-none bg-white dark:bg-gray-800 dark:text-white"
              />
              <p className="text-xs text-gray-500 mt-1">This email will receive notifications and password reset links</p>
            </div>
          </div>
        )}

        {/* Step 2: Resources */}
        {step === 2 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Instance Type *</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {wordpressInstanceTypes.map((instance) => (
                  <button
                    key={instance.id}
                    onClick={() => setConfig({ ...config, instanceType: instance.id })}
                    className={`flex flex-col p-4 border-2 rounded-lg transition-all text-left ${
                      config.instanceType === instance.id
                        ? 'border-black dark:border-white bg-gray-50 dark:bg-gray-700'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-bold text-gray-900 dark:text-white">{instance.name}</h4>
                      <span className="text-lg font-bold text-gray-900 dark:text-white">{instance.price}</span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{instance.description}</p>
                    <p className="text-xs text-gray-500">{instance.cpu} • {instance.ram}</p>
                    {instance.recommended && (
                      <p className="text-xs text-blue-600 mt-2">💡 {instance.recommended}</p>
                    )}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.enableCDN}
                  onChange={(e) => setConfig({ ...config, enableCDN: e.target.checked })}
                  className="w-5 h-5 rounded border-gray-300 text-black focus:ring-black"
                />
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Enable CDN</p>
                  <p className="text-xs text-gray-500">Content delivery network (+$10/mo)</p>
                </div>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.autoScaling}
                  onChange={(e) => setConfig({ ...config, autoScaling: e.target.checked })}
                  className="w-5 h-5 rounded border-gray-300 text-black focus:ring-black"
                />
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Auto-scaling</p>
                  <p className="text-xs text-gray-500">Automatically scale resources</p>
                </div>
              </label>
            </div>
          </div>
        )}

        {/* Step 3: Advanced */}
        {step === 3 && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">PHP Version</label>
                <select
                  value={config.phpVersion}
                  onChange={(e) => setConfig({ ...config, phpVersion: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent outline-none bg-white dark:bg-gray-800 dark:text-white"
                >
                  {phpVersions.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name} {v.recommended ? '(Recommended)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">WordPress Version</label>
                <select
                  value={config.wpVersion}
                  onChange={(e) => setConfig({ ...config, wpVersion: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent outline-none bg-white dark:bg-gray-800 dark:text-white"
                >
                  {wpVersions.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Environment</label>
              <div className="grid grid-cols-3 gap-3">
                {environments.map((env) => (
                  <button
                    key={env.id}
                    onClick={() => setConfig({ ...config, environment: env.id as any })}
                    className={`p-4 border-2 rounded-lg transition-all ${
                      config.environment === env.id
                        ? 'border-black dark:border-white bg-gray-50 dark:bg-gray-700'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <div className="text-2xl mb-2">{env.icon}</div>
                    <div className="font-semibold text-gray-900 dark:text-white text-sm">{env.name}</div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Backup Frequency</label>
              <select
                value={config.backupFrequency}
                onChange={(e) => setConfig({ ...config, backupFrequency: e.target.value as any })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent outline-none bg-white dark:bg-gray-800 dark:text-white"
              >
                <option value="daily">Daily Backups</option>
                <option value="weekly">Weekly Backups</option>
                <option value="none">No Automated Backups</option>
              </select>
            </div>
          </div>
        )}

        {/* Step 4: Security & Review */}
        {step === 4 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Admin Username *</label>
              <input
                type="text"
                value={config.adminUsername}
                onChange={(e) => setConfig({ ...config, adminUsername: e.target.value })}
                placeholder="admin"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent outline-none bg-white dark:bg-gray-800 dark:text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Admin Password *</label>
              <div className="space-y-3">
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={config.adminPassword}
                      onChange={(e) => setConfig({ ...config, adminPassword: e.target.value })}
                      placeholder="Enter a strong password"
                      className="w-full px-4 py-2 pr-24 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent outline-none bg-white dark:bg-gray-800 dark:text-white font-mono text-sm"
                    />
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                      {config.adminPassword && (
                        <button
                          onClick={copyPassword}
                          className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                          title="Copy password"
                        >
                          <CopyOutlined className="text-gray-600 text-sm" />
                        </button>
                      )}
                      <button
                        onClick={() => setShowPassword(!showPassword)}
                        className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                        title="Toggle visibility"
                      >
                        {showPassword ? (
                          <EyeInvisibleOutlined className="text-gray-600 text-sm" />
                        ) : (
                          <EyeOutlined className="text-gray-600 text-sm" />
                        )}
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={generatePassword}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <ReloadOutlined />
                    Generate
                  </button>
                </div>
                
                {/* Password Strength Indicator */}
                {config.adminPassword && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-600 dark:text-gray-400">Password strength:</span>
                      <span className={`text-xs font-semibold ${
                        passwordStrength.label === 'Strong' ? 'text-green-600' :
                        passwordStrength.label === 'Medium' ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {passwordStrength.label}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                      <div
                        className={`h-1.5 rounded-full transition-all duration-300 ${passwordStrength.color}`}
                        style={{ width: `${passwordStrength.strength}%` }}
                      />
                    </div>
                  </div>
                )}
                
                {config.adminPassword && config.adminPassword.length < 8 && (
                  <p className="text-xs text-red-600">⚠️ Password must be at least 8 characters</p>
                )}
                
                <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                  <p className="text-xs text-gray-600 dark:text-gray-400 font-medium mb-1">Strong password requirements:</p>
                  <ul className="text-xs text-gray-500 space-y-0.5 ml-4 list-disc">
                    <li>At least 8 characters (12+ recommended)</li>
                    <li>Mix of uppercase and lowercase letters</li>
                    <li>Include numbers and special characters</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2 text-sm">Configuration Summary</h4>
              <div className="space-y-1 text-xs text-blue-800">
                <p>• Site: {config.name || 'Not set'}</p>
                <p>• Domain: {config.domain || 'Not set'}</p>
                <p>• Instance: {wordpressInstanceTypes.find(i => i.id === config.instanceType)?.name || 'Not set'}</p>
                <p>• CDN: {config.enableCDN ? 'Enabled' : 'Disabled'}</p>
                <p>• Environment: {config.environment}</p>
                <p>• SSL: Always enabled</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={handleBack}
          disabled={step === 1}
          className="px-6 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Back
        </button>
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/resources')}
            className="px-6 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          {step < 4 ? (
            <button
              onClick={handleNext}
              disabled={!isStepValid()}
              className="px-6 py-2.5 bg-black text-white dark:bg-white dark:text-black rounded-lg font-medium hover:bg-dark-bg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleCreate}
              disabled={!isStepValid()}
              className="px-6 py-2.5 bg-black text-white dark:bg-white dark:text-black rounded-lg font-medium hover:bg-dark-bg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Create WordPress Site
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateWordPress;